import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  isLoading?: boolean;
  trend?: number;
  color?: 'blue' | 'green' | 'orange' | 'purple' | 'red';
}

const colorStyles = {
  blue: {
    bg: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    trend: 'text-blue-600',
  },
  green: {
    bg: 'bg-green-50',
    icon: 'bg-green-100 text-green-600',
    trend: 'text-green-600',
  },
  orange: {
    bg: 'bg-orange-50',
    icon: 'bg-orange-100 text-orange-600',
    trend: 'text-orange-600',
  },
  purple: {
    bg: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    trend: 'text-purple-600',
  },
  red: {
    bg: 'bg-red-50',
    icon: 'bg-red-100 text-red-600',
    trend: 'text-red-600',
  },
};

export function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
  trend,
  color = 'blue',
}: KPICardProps) {
  const styles = colorStyles[color];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl shadow-sm border border-gray-200 p-6', styles.bg)}>
      <div className="flex items-center gap-4">
        <div className={cn('p-3 rounded-lg', styles.icon)}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 whitespace-nowrap">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 whitespace-nowrap">{subtitle}</p>
          )}
        </div>
      </div>
      {trend !== undefined && (
        <div className={cn('mt-4 text-sm font-medium', styles.trend)}>
          {trend > 0 ? '+' : ''}{trend}%
        </div>
      )}
    </div>
  );
}
